<script lang="ts" setup>
  import { useClock } from '@/composables/use-clock';
  import { AttendanceLocation } from '@/services/api/attendance-activity-api.ts';
  import {
    loadAndErrorHandleAsync,
    loadWithStatusAndErrorHandleAsync,
  } from '@/services/loading-svc.ts';
  import { useActivityStore } from '@/stores/activity.ts';
  import { useAuthStore } from '@/stores/auth.ts';
  import { useThemeStore } from '@/stores/theme';
  import { LocationTypes } from '@/utils/item-printer.ts';
  import { useDocumentVisibility } from '@vueuse/core';
  import dayjs from 'dayjs';
  import { onMounted, ref, watch } from 'vue';

  const authStore = useAuthStore();
  const activityStore = useActivityStore();
  const { dateFormat } = useClock();
  const isPageVisible = useDocumentVisibility();

  const loading = ref<boolean>(false);
  const isModalStartOn = ref(false);
  const isModalAwayOn = ref(false);
  const isModalEndOn = ref(false);

  const themeStore = useThemeStore();

  const location = ref<AttendanceLocation>('INSIDE');
  const comment = ref<string>();
  const monthlyStatusRefresh = ref<boolean>(false);

  function hideAllModals() {
    isModalStartOn.value = false;
    isModalAwayOn.value = false;
    isModalEndOn.value = false;
    monthlyStatusRefresh.value = true;
  }

  function onChangeMonthlyStatusRefresh() {
    monthlyStatusRefresh.value = false;
  }

  async function refresh() {
    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        await activityStore.updateSummary();
      }
    );
  }

  /** 페이지가 보여질 때마다 자동 새로고침 */
  watch(isPageVisible, (newValue, oldValue) => {
    if (newValue === 'visible' && oldValue === 'hidden') {
      refresh();
    }
  });

  onMounted(() => {
    refresh();
  });

  async function startWork() {
    await loadAndErrorHandleAsync(async () => {
      await activityStore.startWorkAsync(location.value);
      hideAllModals();
    });
  }

  async function restartWork() {
    await loadAndErrorHandleAsync(async () => {
      await activityStore.changeWorkStatusAsync('WORK_START', location.value);
      hideAllModals();
    });
  }

  async function startAway() {
    await loadAndErrorHandleAsync(async () => {
      await activityStore.changeWorkStatusAsync('AWAY_START');
      hideAllModals();
    });
  }

  async function endAway() {
    await loadAndErrorHandleAsync(async () => {
      await activityStore.changeWorkStatusAsync('AWAY_END', location.value);
      hideAllModals();
    });
  }

  function openEndWork() {
    if (!activityStore.summary) return;
    isModalEndOn.value = true;
  }

  async function endWork() {
    await loadAndErrorHandleAsync(async () => {
      await activityStore.changeWorkStatusAsync(
        'WORK_END',
        'INSIDE',
        comment.value
      );
      hideAllModals();
      comment.value = undefined;
    });
  }
</script>

<template>
  <DefaultLayout title="홈">
    <skeleton
      v-if="loading"
      class="my-5" />
    <div v-else>
      <h2 class="-mt-2 ml-1 leading-none text-gray-500 text-md">
        {{ dateFormat.fullDate }}
        <WorkingBadge :status="activityStore.lastAction" />
      </h2>
      <div class="mt-5">
        <div
          :class="{
            'bg-base-100': !activityStore.isWorking,
            'bg-emerald-200 border-emerald-300':
              activityStore.isWorking && themeStore.isLight,
            'border-slate-700 bg-neutral': themeStore.isDark,
          }"
          class="w-full border shadow-md card">
          <div class="p-5 card-body">
            <!-- prettier-ignore -->
            <p>
              {{ authStore.user.department }} <span class="font-bold">{{ authStore.user.name }}</span>님
            </p>

            <!-- 쉬기 or 근무 종료 -->
            <div v-if="activityStore.isWorking">
              <WorkingStat />
              <div class="flex items-center justify-center gap-x-2">
                <button
                  class="w-3/6 text-base btn rounded-xl"
                  @click="isModalAwayOn = true">
                  🥱 잠시 쉬러가기
                </button>
                <button
                  class="w-3/6 text-base text-white btn btn-success bg-emerald-500 rounded-xl"
                  @click="openEndWork">
                  😎 근무 종료하기
                </button>
              </div>
            </div>

            <!-- 근무 복귀 -->
            <div v-if="activityStore.isAway">
              <WorkingStat />
              <div class="flex items-center justify-center gap-x-2">
                <button
                  class="mt-1 text-base btn btn-block btn-info"
                  @click="isModalAwayOn = true">
                  🤩 근무 복귀하기
                </button>
              </div>
            </div>

            <!-- 근무 시작 -->
            <div v-else-if="activityStore.canStartToday">
              <button
                class="mt-1 text-base btn btn-block btn-primary"
                @click="isModalStartOn = true">
                🚀 오늘의 근무 시작하기
              </button>
            </div>

            <div v-else-if="activityStore.isEnded">
              <WorkingStat />
              <button
                class="mt-1 text-base btn btn-block btn-error"
                @click="isModalStartOn = true">
                😇 근무 재시작하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 이번달 요약 -->
    <div class="mt-5 pt-4 pb-3">
      <MonthlyStatus
        :refresh="monthlyStatusRefresh"
        @on-change="onChangeMonthlyStatusRefresh" />
    </div>
  </DefaultLayout>

  <!-- 근무 시작 / 재시작 -->
  <AppModal
    id="start_work"
    :is-visible="isModalStartOn"
    has-close-x
    is-bottom-on-mobile
    @close="isModalStartOn = false">
    <h3 class="text-lg font-bold">{{ dateFormat.simpleDate }}</h3>
    <p class="py-4 text-xl text-center">
      <span
        :class="{
          'text-info': activityStore.canStartToday,
          'text-error': !activityStore.canStartToday,
        }"
        class="-mr-1 text-3xl font-bold">
        {{ dateFormat.hourMinute }}
      </span>
      <!-- prettier-ignore -->
      <span v-if="activityStore.canStartToday">부터 <br /> 근무를 시작합니다.</span>
      <!-- prettier-ignore -->
      <span v-else><br />근무 재시작합니다.</span>
    </p>
    <div
      v-if="activityStore.summary?.vacationType"
      class="text-sm text-center mb-4">
      <template v-if="activityStore.summary?.vacationType == 'ALL'">
        오늘은
        <span class="-mr-1 font-bold">전일 휴가</span>
        입니다.
        <br />
        실제 근무하는 경우에만 기록하세요.
      </template>
      <!-- prettier-ignore -->
      <template
        v-else-if="
          activityStore.summary?.vacationType == 'MORNING' &&
            dayjs().isAfter(dayjs().hour(6).minute(0).second(0)) &&
            dayjs().isBefore(dayjs().hour(14).minute(30).second(0))
        ">
        오늘은
        <span class="-mr-1 font-bold">오전 반차</span>
        입니다.
        <br />
        실제 근무하는 경우에만 기록하세요.
      </template>
      <!-- prettier-ignore -->
      <template
        v-else-if="
          activityStore.summary?.vacationType == 'AFTERNOON' &&
            dayjs().isAfter(dayjs().hour(14).minute(30).second(0)) &&
            dayjs().isBefore(dayjs().hour(18).minute(30).second(0))
        ">
        오늘은
        <span class="-mr-1 font-bold">오후 반차</span>
        입니다.
        <br />
        실제 근무하는 경우에만 기록하세요.
      </template>
    </div>
    <select
      v-model="location"
      class="w-full select select-bordered mb-4">
      <option
        disabled
        selected>
        근무 위치
      </option>
      <option
        v-for="type in LocationTypes"
        :key="type.value"
        :value="type.value">
        {{ type.text }}
      </option>
    </select>
    <template #action>
      <div class="text-center">
        <button
          v-if="activityStore.canStartToday"
          class="text-lg btn btn-block btn-primary"
          @click="startWork">
          😉 오늘의 근무 시작
        </button>
        <button
          v-else
          class="text-lg btn btn-block btn-error"
          @click="restartWork">
          😇 근무 재시작
        </button>
      </div>
    </template>
  </AppModal>

  <!-- 쉬러가기 / 복귀하기 -->
  <AppModal
    id="away_start_end"
    :is-visible="isModalAwayOn"
    has-close-x
    is-bottom-on-mobile
    @close="isModalAwayOn = false">
    <h3 class="text-lg font-bold">{{ dateFormat.simpleDate }}</h3>
    <p class="py-4 text-xl text-center">
      <span class="-mr-1 text-3xl font-bold text-error">
        {{ dateFormat.hourMinute }}
      </span>
      부터
      <br />
      <span v-if="activityStore.isAway">근무를 복귀합니다.</span>
      <span v-else>근무외 시간입니다.</span>
    </p>
    <select
      v-if="activityStore.isAway"
      v-model="location"
      class="w-full select select-bordered mb-4">
      <option
        disabled
        selected>
        근무 위치
      </option>
      <option
        v-for="type in LocationTypes"
        :key="type.value"
        :value="type.value">
        {{ type.text }}
      </option>
    </select>
    <template #action>
      <div class="text-center">
        <button
          v-if="activityStore.isAway"
          class="text-lg btn btn-block btn-info"
          @click="endAway">
          🤩 근무 복귀하기
        </button>
        <button
          v-else
          class="text-lg btn btn-block"
          @click="startAway">
          🥱 잠시 쉬러가기
        </button>
      </div>
    </template>
  </AppModal>

  <!-- 근무 종료 -->
  <AppModal
    id="end_work"
    :is-visible="isModalEndOn"
    has-close-x
    is-bottom-on-mobile
    @close="isModalEndOn = false">
    <h3 class="text-lg font-bold">{{ dateFormat.simpleDate }}</h3>
    <p class="py-4 text-xl text-center">
      <span class="-mr-1 text-3xl font-bold text-success">
        {{ dateFormat.hourMinute }}
      </span>
      <br />
      근무를 종료합니다.
    </p>
    <textarea
      v-model="comment"
      class="w-full textarea textarea-bordered mb-4"
      placeholder="근무 코멘트를 입력해주세요. (선택)" />
    <template #action>
      <div class="text-center">
        <button
          class="text-lg btn btn-block btn-success"
          @click="endWork">
          😎 근무 종료
        </button>
      </div>
    </template>
  </AppModal>
</template>
